'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

export type SearchableSelectOption = {
  value: string | number;
  label: string;
  sublabel?: string;
  disabled?: boolean;
};

type SearchableSelectProps = {
  options: SearchableSelectOption[];
  value: string | number | undefined | null;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  emptyText?: string;
  ariaLabel?: string;
};

export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Seleccionar...',
  searchPlaceholder = 'Buscar...',
  disabled = false,
  required = false,
  className = '',
  emptyText = 'No se encontraron resultados',
  ariaLabel,
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  const filteredOptions = options.filter((opt) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      opt.label.toLowerCase().includes(term) ||
      (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
    );
  });

  const handleSelect = (val: string | number) => {
    onChange(String(val));
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Hidden input for HTML5 required validity if used inside native form */}
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ opacity: 0, width: 0, height: 0, position: 'absolute', pointerEvents: 'none' }}
          value={value ? String(value) : ''}
          onChange={() => {}}
          required={required}
        />
      )}

      {/* Main Trigger Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={ariaLabel || placeholder}
        aria-expanded={isOpen}
        className={`flex h-11 w-full items-center justify-between rounded-lg border border-[#c2ceda] bg-white px-3.5 text-left text-sm transition outline-none focus:border-[#004A91] focus:ring-1 focus:ring-[#004A91] ${
          disabled ? 'cursor-not-allowed bg-[#eef2f5] text-[#9aacbf]' : 'text-gray-800'
        } ${isOpen ? 'border-[#004A91] ring-1 ring-[#004A91]' : ''}`}
      >
        <span className="truncate pr-2">
          {selectedOption ? (
            <span className="font-medium text-[#111827]">
              {selectedOption.label}
              {selectedOption.sublabel && (
                <span className="ml-1.5 text-xs text-gray-500 font-normal">
                  {selectedOption.sublabel}
                </span>
              )}
            </span>
          ) : (
            <span className="text-[#8ba5bd]">{placeholder}</span>
          )}
        </span>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {selectedOption && !disabled && !required && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onChange('');
                }
              }}
              title="Limpiar selección"
              className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 text-[#8ba5bd] transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-[#004A91]' : ''
            }`}
          />
        </div>
      </button>

      {/* Dropdown overlay */}
      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 max-h-72 w-full rounded-xl border border-[#cbd8e4] bg-white shadow-[0_12px_28px_rgba(15,23,42,0.15)] flex flex-col overflow-hidden animate-in fade-in-50 duration-150">
          {/* Search box inside dropdown */}
          <div className="border-b border-[#e2e8f0] p-2 bg-[#f8fafc]">
            <div className="relative flex items-center">
              <Search className="pointer-events-none absolute left-2.5 h-4 w-4 text-[#8ba5bd]" />
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="h-9 w-full rounded-md border border-[#cbd8e4] bg-white pl-8 pr-3 text-xs text-gray-800 placeholder-[#8ba5bd] outline-none focus:border-[#004A91] focus:ring-1 focus:ring-[#004A91]"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-56 overflow-y-auto py-1 text-sm">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-center text-xs text-gray-400">
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const isSelected = String(opt.value) === String(value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => handleSelect(opt.value)}
                    className={`flex w-full flex-col px-3.5 py-2 text-left text-xs transition ${
                      opt.disabled
                        ? 'cursor-not-allowed opacity-40 bg-gray-50'
                        : isSelected
                        ? 'bg-[#eef5fa] text-[#004A91] font-bold'
                        : 'text-gray-700 hover:bg-[#f1f5f9]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="truncate">{opt.label}</span>
                      {isSelected && (
                        <span className="ml-2 text-[10px] uppercase font-bold text-[#004A91] tracking-wider">
                          ✓
                        </span>
                      )}
                    </div>
                    {opt.sublabel && (
                      <span className="mt-0.5 text-[11px] text-gray-500 font-normal truncate">
                        {opt.sublabel}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
